import { create } from "zustand";
import {
  stringToTicketPhase,
  TicketPhase,
} from "../assets/js/AppTicketConstant";
import AppUserProfile, { UserType } from "../assets/js/AppUserProfile";

const useSubTicketStore = create((set, get) => ({
  proposalSubTickets: {},
  claimSubTickets: {},
  managementSubTicket: {},
  accountingSubTicket: {},
  rejectSubTickets: {},

  storeAlreadyApproveTicket: ({ phase, subTickets }) => {
    const state = get();
    const approvedSubTickets = subTickets.filter(
      (subTicket) => subTicket.status === "APPROVED"
    );

    approvedSubTickets.forEach((subTicket) => {
      const reviewObject = {
        sub_ticket_id: Number(subTicket.id),
        description: "",
        status: "APPROVED",
      };

      switch (stringToTicketPhase(phase)) {
        case TicketPhase.Proposal:
          state.setProposalSubTickets({
            subTicketId: subTicket.id,
            data: reviewObject,
          });
          break;
        case TicketPhase.Claim:
          state.setClaimSubTickets({
            subTicketId: subTicket.id,
            data: reviewObject,
          });
          break;
        case TicketPhase.Management:
          state.setManagementSubTickets({
            subTicketId: subTicket.id,
            data: reviewObject,
          });
          break;
        case TicketPhase.Accounting:
          state.setAccountingSubTickets({
            subTicketId: subTicket.id,
            data: reviewObject,
          });
          break;
        default:
          break;
      }
    });
  },

  setReviewTicket: ({ phase, subTicketId, data }) => {
    const state = get();
    switch (stringToTicketPhase(phase)) {
      case TicketPhase.Proposal:
        state.setProposalSubTickets({ subTicketId: subTicketId, data: data });
        break;
      case TicketPhase.Claim:
        state.setClaimSubTickets({ subTicketId: subTicketId, data: data });
        break;
      case TicketPhase.Management:
        state.setManagementSubTickets({ subTicketId: subTicketId, data: data });
        break;
      case TicketPhase.Accounting:
        state.setAccountingSubTickets({ subTicketId: subTicketId, data: data });
        break;
      default:
        break;
    }
  },

  setProposalSubTickets: ({ subTicketId, data }) => {
    set((state) => {
      return {
        proposalSubTickets: {
          ...state.proposalSubTickets,
          [subTicketId]: data,
        },
      };
    });
  },

  setClaimSubTickets: ({ subTicketId, data }) => {
    set((state) => {
      return {
        claimSubTickets: {
          ...state.claimSubTickets,
          [subTicketId]: data,
        },
      };
    });
  },

  setManagementSubTickets: ({ subTicketId, data }) => {
    set((state) => {
      return {
        managementSubTicket: {
          ...state.managementSubTicket,
          [subTicketId]: data,
        },
      };
    });
  },

  setAccountingSubTickets: ({ subTicketId, data }) => {
    set((state) => {
      return {
        accountingSubTicket: {
          ...state.accountingSubTicket,
          [subTicketId]: data,
        },
      };
    });
  },

  setRejectSubTickets: ({ subTicketId, data }) => {
    set((state) => {
      return {
        rejectSubTickets: {
          ...state.rejectSubTickets,
          [subTicketId]: data,
        },
      };
    });
  },

  areAllProposalTicketsSelected: (ticketDataArray) => {
    const state = get();
    return ticketDataArray.every(
      (ticket) => ticket.id in state.proposalSubTickets
    );
  },

  areAllClaimTicketsSelected: (ticketDataArray) => {
    const state = get();
    return ticketDataArray.every(
      (ticket) => ticket.id in state.claimSubTickets
    );
  },

  areAllManagementTicketsSelected: (ticketDataArray) => {
    const state = get();
    return ticketDataArray.every(
      (ticket) => ticket.id in state.managementSubTicket
    );
  },

  areAllAccountingTicketsSelected: (ticketDataArray) => {
    const state = get();
    return ticketDataArray.every(
      (ticket) => ticket.id in state.accountingSubTicket
    );
  },

  areAllRejectedTicketsSelected: (ticketDataArray) => {
    const state = get();
    return ticketDataArray.every(
      (ticket) => ticket.id in state.rejectSubTickets
    );
  },

  getProposalTicket: (ticketId) => {
    const state = get();
    return state.proposalSubTickets[ticketId] || null;
  },

  getRejectTicket: (ticketId) => {
    const state = get();
    return state.rejectSubTickets[ticketId] || null;
  },

  hasRejectedSubTicket: ({ ticketDataArray, phase }) => {
    const state = get();
    const phaseMapping = {
      [TicketPhase.Claim.value]: state.claimSubTickets,
      [TicketPhase.Accounting.value]: state.accountingSubTicket,
      [TicketPhase.Management.value]: state.managementSubTicket,
      [TicketPhase.Proposal.value]: state.proposalSubTickets,
    };

    const subTicketPhase = phaseMapping[phase.value];
    if (!subTicketPhase) return false;

    return ticketDataArray
      .map((item) => subTicketPhase[item.id])
      .some((ticket) => ticket?.status === "REJECTED");
  },

  alreadyReviewed: ({ subTicketId, phase }) => {
    const state = get();
    const userRole = AppUserProfile.getData()["whoami"];
    const phaseMapping = {
      [TicketPhase.Claim.value]: state.claimSubTickets,
      [TicketPhase.Accounting.value]: state.accountingSubTicket,
      [TicketPhase.Management.value]: state.managementSubTicket,
      [TicketPhase.Proposal.value]: state.proposalSubTickets,
    };
    let reviewObjects;
    let reviewStatus;
    if (
      userRole === UserType.DEALER_BYD ||
      userRole === UserType.DEALER_DENZA
    ) {
      reviewObjects = state.rejectSubTickets[subTicketId];
      if (reviewObjects) {
        reviewStatus = "Edit";
      } else {
        reviewObjects = phaseMapping?.[phase.value];
        reviewStatus = reviewObjects?.[subTicketId]?.status;
      }
    } else {
      reviewObjects = phaseMapping[phase.value];
      reviewStatus = reviewObjects[subTicketId]?.status;
    }

    return reviewStatus || null;
  },
}));

export default useSubTicketStore;
